/**
 * MuscleVolumeChart — Rolling 8-week hard-set volume per muscle group
 *
 * Shows a horizontal bar chart of weekly hard sets for each tracked muscle,
 * colour-coded by recovery status vs. the weekly volume target.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';
import { db } from '@/lib/db';
import type { MuscleGroup } from '@/types';

const MUSCLE_LABELS: Partial<Record<MuscleGroup, string>> = {
  chest:        'Chest',
  front_delt:   'Front Delt',
  lateral_delt: 'Lateral Delt',
  rear_delt:    'Rear Delt',
  triceps:      'Triceps',
  biceps:       'Biceps',
  lats:         'Lats',
  rhomboids:    'Rhomboids',
  traps:        'Traps',
  lower_back:   'Lower Back',
  quads:        'Quads',
  hamstrings:   'Hamstrings',
  glutes:       'Glutes',
  calves:       'Calves',
  core:         'Core',
};

export default function MuscleVolumeChart() {
  const [weeks, setWeeks] = useState(4);

  const data = useLiveQuery(async () => {
    const settings = await db.settings.get('singleton');
    const target   = settings?.weeklyVolumeTarget ?? 15;

    const cutoff = new Date(Date.now() - weeks * 7 * 86_400_000).toISOString();
    const sets   = await db.loggedSets
      .where('loggedAt').aboveOrEqual(cutoff)
      .filter((s) => !s.isWarmup && s.rpe >= 7)
      .toArray();

    const exIds = [...new Set(sets.map((s) => s.exerciseId))];
    const exercises = await db.exercises.bulkGet(exIds);
    const exMap = new Map(exercises.filter(Boolean).map((e) => [e!.id, e!]));

    const vol = new Map<MuscleGroup, number>();
    for (const s of sets) {
      const ex = exMap.get(s.exerciseId);
      if (!ex) continue;
      for (const alloc of ex.muscles) {
        vol.set(alloc.muscle, (vol.get(alloc.muscle) ?? 0) + alloc.fraction);
      }
    }

    // Average per week
    return Array.from(vol.entries())
      .map(([muscle, total]) => ({
        muscle,
        label: MUSCLE_LABELS[muscle] ?? muscle.replace(/_/g, ' '),
        setsPerWeek: Math.round((total / weeks) * 10) / 10,
        target,
      }))
      .filter((d) => d.setsPerWeek >= 0.5)
      .sort((a, b) => b.setsPerWeek - a.setsPerWeek);
  }, [weeks]);

  if (!data || data.length === 0) return null;

  const maxSets = Math.max(...data.map((d) => d.setsPerWeek), data[0]?.target ?? 15);

  return (
    <section>
      <div className="flex items-center justify-between mb-1">
        <p className="section-header mb-0">Avg Weekly Volume by Muscle</p>
        {/* Week toggle */}
        <div className="flex bg-surface-300 rounded-xl p-0.5 gap-0.5">
          {[2, 4, 8].map((w) => (
            <button
              key={w}
              onClick={() => setWeeks(w)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors
                ${weeks === w ? 'bg-accent text-white' : 'text-ink-muted'}`}
            >
              {w}w
            </button>
          ))}
        </div>
      </div>

      <div className="card space-y-2">
        {data.map(({ muscle, label, setsPerWeek, target }) => {
          const pct = Math.min((setsPerWeek / maxSets) * 100, 100);
          const color =
            setsPerWeek >= target     ? 'bg-accent' :
            setsPerWeek >= target * 0.5 ? 'bg-success' :
                                          'bg-surface-500';
          return (
            <div key={muscle}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-ink-muted">{label}</span>
                <span className={`text-xs font-semibold font-mono
                  ${setsPerWeek >= target ? 'text-accent' : setsPerWeek >= target * 0.5 ? 'text-success' : 'text-ink-faint'}`}>
                  {setsPerWeek} / {target}
                </span>
              </div>
              <div className="h-2 bg-surface-400 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${color}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
        <p className="text-xs text-ink-faint pt-1">
          Target: {data[0]?.target} sets/week per muscle · RPE ≥ 7 only
        </p>
      </div>
    </section>
  );
}
